import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import {
  Badge,
  Button,
  Card,
  EditPane,
  Field,
  Input,
  MultiSelect,
  Select,
  toast,
  useConfirm,
} from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { FolderPlus, Save, Trash2, Upload } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ARTWORK_EDITIONS,
  ARTWORK_STATUSES,
  ASPECT_RATIO_STRATEGIES,
  useApi,
  type ArtworkDTO,
  type ArtworkImageDTO,
  type ArtworkTagDTO,
  type ProductEntityDTO,
  type ProductImageDTO,
  type StoreDTO,
} from "../../api";
import {
  ARTWORK_IMAGE_VARIANT,
  ARTWORK_TAG_VARIANT,
  DISTRIBUTION_VARIANT,
  PRODUCT_FAMILY_VARIANT,
  PRODUCT_STATUS_VARIANT,
} from "../../components/badges";
import { CopyUrl, EnumBadge, objectUrl, Thumbnail, TwoLine } from "../../components/cells";
import { crudEditor, selectField, textField } from "../../components/editor";
import { AccessDenied, ErrorState, useAllowed } from "../../components/GuardedPage";
import { LogicView } from "../../views";
import { DisplayCollectionPickerDialog } from "./dialogs";

/**
 * One artwork: its metadata, the products generated from it, its tags, its
 * rendered images and its listing state on every storefront.
 *
 * Tags are edited in place but only persisted when the whole artwork is saved
 * — they live on the `ArtworkDTO`, so a tag change is an unsaved edit to the
 * artwork, exactly as the Vaadin tab behaved.
 */
export function ArtworkDetailPage() {
  const { t } = useTranslation();
  const api = useApi();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const { id } = useParams({ from: "/artworks/$id" });
  const artworkId = Number(id);
  const allowed = useAllowed(LogicView.ARTWORKS);
  const title = t("navigation.item.artworks", "Artworks");

  const [draft, setDraft] = useState<ArtworkDTO | null>(null);
  const [saving, setSaving] = useState(false);

  const artwork = useQuery({
    queryKey: ["artwork", artworkId],
    queryFn: () => api.artworks.findById(artworkId),
    enabled: allowed && Number.isFinite(artworkId),
  });

  const stores = useQuery({
    queryKey: ["store"],
    queryFn: () => api.stores.findAll(),
    enabled: allowed,
  });

  const catalogs = useQuery({
    queryKey: ["catalog"],
    queryFn: () => api.catalogs.findAll(),
    enabled: allowed,
  });

  const collections = useQuery({
    queryKey: ["collection"],
    queryFn: () => api.collections.findAll(),
    enabled: allowed,
  });

  const publishers = useQuery({
    queryKey: ["publisher"],
    queryFn: () => api.publishers.findAll(),
    enabled: allowed,
  });

  // One query feeds both product tabs. `/product/entity` omits the image
  // multiset, which left the Products tab's thumbnail column permanently blank;
  // the with-images endpoint is the same projection plus those images.
  const products = useQuery({
    queryKey: ["artwork", artworkId, "product-entities"],
    queryFn: () => api.productEntitiesWithImages.getByArtwork(artworkId),
    enabled: allowed && Number.isFinite(artworkId),
  });

  useEffect(() => {
    if (artwork.data) setDraft(structuredClone(artwork.data));
  }, [artwork.data]);

  // The draft is the edit buffer for every tab — metadata fields and the tag
  // grid alike — so comparing it against the fetched artwork is what tells us
  // whether Save has anything to write.
  const dirty = useMemo(
    () => Boolean(draft && artwork.data && JSON.stringify(draft) !== JSON.stringify(artwork.data)),
    [draft, artwork.data],
  );

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["artwork", artworkId] });
    await queryClient.invalidateQueries({ queryKey: ["artwork", "lazy"] });
  };

  const save = async () => {
    if (!draft || !dirty) return;
    const ok = await confirm({
      title: t("dialog.confirm.save.title", "Confirm save"),
      message: t("dialog.confirm.save.text", "Are you sure you want to save?"),
    });
    if (!ok) return;
    setSaving(true);
    try {
      await api.artworks.update(draft);
      toast.success(t("notification.save.success", "Item saved successfully"));
      await refresh();
    } catch (failure) {
      toast.error(
        failure instanceof Error ? failure.message : t("notification.save.error", "Failed to save item"),
      );
    } finally {
      setSaving(false);
    }
  };

  /**
   * Answers the header's "may I leave?" — it does the navigating itself, so
   * this only decides. Returning false keeps the user on the page.
   */
  const leave = async (): Promise<boolean> => {
    if (!dirty) return true;
    return confirm({
      title: t("dialog.confirm.leave.title", "Discard unsaved changes?"),
      message: t("dialog.confirm.leave.text", "Your edits to this artwork have not been saved."),
      variant: "danger",
      confirmLabel: t("dialog.confirm.discard", "Discard"),
    });
  };

  if (!allowed) return <AccessDenied title={title} />;
  if (artwork.error) return <ErrorState title={title} error={artwork.error} />;

  const entity = draft;
  const storeList = stores.data ?? [];

  return (
    <>
      <PageHeader
        parent={{ to: "/artworks", label: t("navigation.item.artworks", "Artworks"), beforeLeave: leave, }}
        title={`${entity?.metadata.code ?? `#${artworkId}`}`}
        badges={
          entity ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge variant="accent">{`${entity.metadata.code} - ${entity.metadata.title}`}</Badge>
              <Badge>{entity.catalog?.name}</Badge>
              <Badge>{entity.metadata.orientation}</Badge>
              <Badge>{entity.metadata.ratio}</Badge>
              <Badge variant="success">
                {`Max: ${entity.metadata.max_print_width} x ${entity.metadata.max_print_height}`}
              </Badge>
              {dirty && (
                <Badge variant="warning" size="sm">
                  {t("badge.unsaved", "Unsaved")}
                </Badge>
              )}
            </div>
          ) : undefined
        }
        actions={
          <div className="flex items-center gap-2">
<Button
              size="sm"
              variant="success"
              loading={saving}
              disabled={!dirty}
              icon={<Save className="size-3.5" />}
              onClick={() => void save()}
            >
              {t("button.save", "Save")}
            </Button>
          </div>
        }
      />

      {entity && (
        <EditPane
          tabs={[
            {
              id: "metadata",
              label: t("tab.metadata", "Metadata"),
              content: (
                <MetadataTab
                  artwork={entity}
                  onChange={setDraft}
                  catalogs={(catalogs.data ?? []).map((entry) => ({
                    value: String(entry.id),
                    label: entry.name,
                  }))}
                  collections={(collections.data ?? []).map((entry) => ({
                    value: String(entry.id),
                    label: entry.name,
                  }))}
                  allCollections={collections.data ?? []}
                  publisherName={
                    (publishers.data ?? []).find((entry) => entry.id === entity.publisher?.id)?.name ??
                    entity.publisher?.name ??
                    ""
                  }
                  allCatalogs={catalogs.data ?? []}
                />
              ),
            },
            {
              id: "products",
              flush: true,
              label: t("tab.products", "Products"),
              badge: <Badge size="sm">{products.data?.length ?? 0}</Badge>,
              content: <ProductsTab rows={products.data ?? []} loading={products.isLoading} />,
            },
            {
              id: "tags",
              flush: true,
              label: t("tab.tags", "Tags"),
              badge: <Badge size="sm">{entity.tags?.length ?? 0}</Badge>,
              content: (
                <TagsTab
                  tags={entity.tags ?? []}
                  onChange={(tags) => setDraft({ ...entity, tags })}
                  artworkId={entity.id}
                />
              ),
            },
            {
              id: "images",
              flush: true,
              label: t("tab.images", "Images"),
              badge: <Badge size="sm">{entity.images?.length ?? 0}</Badge>,
              content: (
                <ImagesTab
                  artwork={entity}
                  onChanged={() => void refresh()}
                />
              ),
            },
            {
              id: "product-images",
              flush: true,
              label: t("tab.product-images", "Product images"),
              content: (
                <ProductImagesTab entities={products.data ?? []} loading={products.isLoading} />
              ),
            },
            ...storeList.map((store) => ({
              id: `store-${store.id}`,
              label: store.name,
              content: <ShopifyTab artwork={entity} store={store} />,
            })),
          ]}
        />
      )}
    </>
  );
}

// ── metadata ──────────────────────────────────────────────────────────────

function MetadataTab({
  artwork,
  onChange,
  catalogs,
  collections,
  allCollections,
  allCatalogs,
  publisherName,
}: {
  artwork: ArtworkDTO;
  onChange: (next: ArtworkDTO) => void;
  catalogs: Array<{ value: string; label: string }>;
  collections: Array<{ value: string; label: string }>;
  allCollections: Array<{ id: number; name: string }>;
  allCatalogs: Array<{ id: number; name: string }>;
  publisherName: string;
}) {
  const { t } = useTranslation();
  const metadata = artwork.metadata;

  const setMetadata = (changes: Partial<typeof metadata>) =>
    onChange({ ...artwork, metadata: { ...metadata, ...changes } });

  return (
    <div className="flex flex-col gap-4">
      <Card className="p-5">
        <h3 className="mb-4 text-[13px] font-semibold text-ink">
          {t("artwork.section.identity", "Identity")}
        </h3>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          <Field label={t("form.field.uuid", "UUID")}>
            <Input value={artwork.uuid ?? ""} readOnly disabled />
          </Field>
          <Field label={t("form.field.code", "Code")}>
            <Input value={metadata.code} readOnly disabled />
          </Field>
          <Field label={t("form.field.publisher-code", "Publisher code")} required>
            <Input
              value={metadata.publisher_code ?? ""}
              onChange={(event) => setMetadata({ publisher_code: event.target.value.toUpperCase() })}
            />
          </Field>
          <Field label={t("form.field.publisher", "Publisher")}>
            <Input value={publisherName} readOnly disabled />
          </Field>
          <Field label={t("form.field.status", "Status")} required>
            <Select
              value={artwork.status}
              options={ARTWORK_STATUSES.map((entry) => ({ value: entry, label: entry }))}
              onChange={(event) =>
                onChange({ ...artwork, status: event.target.value as ArtworkDTO["status"] })
              }
            />
          </Field>
          <Field label={t("form.field.edition", "Edition")} required>
            <Select
              value={metadata.edition}
              options={ARTWORK_EDITIONS.map((entry) => ({ value: entry, label: entry }))}
              onChange={(event) =>
                setMetadata({ edition: event.target.value as typeof metadata.edition })
              }
            />
          </Field>
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="mb-4 text-[13px] font-semibold text-ink">
          {t("artwork.section.description", "Description")}
        </h3>
        <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-4">
          <Field label={t("form.field.title", "Title")} required className="sm:col-span-2">
            <Input
              value={metadata.title ?? ""}
              onChange={(event) => setMetadata({ title: event.target.value })}
            />
          </Field>
          <Field label={t("form.field.artist", "Artist")}>
            <Input
              value={metadata.artist ?? ""}
              onChange={(event) => setMetadata({ artist: event.target.value })}
            />
          </Field>
          <Field label={t("form.field.catalog", "Catalog")} required>
            <Select
              value={artwork.catalog ? String(artwork.catalog.id) : ""}
              options={catalogs}
              onChange={(event) => {
                const next = allCatalogs.find((entry) => String(entry.id) === event.target.value);
                if (next) onChange({ ...artwork, catalog: { id: next.id, name: next.name } });
              }}
            />
          </Field>
          <Field label={t("form.field.collection", "Collection")} className="sm:col-span-2">
            <MultiSelect
              options={collections}
              value={(artwork.collections ?? []).map((entry) => String(entry.id))}
              onChange={(values) =>
                onChange({
                  ...artwork,
                  collections: allCollections
                    .filter((entry) => values.includes(String(entry.id)))
                    .map((entry) => ({ id: entry.id, name: entry.name })),
                })
              }
            />
          </Field>
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="mb-4 text-[13px] font-semibold text-ink">
          {t("artwork.section.print", "Print")}
        </h3>
        <div className="grid gap-4 sm:grid-cols-2 2xl:grid-cols-4">
          <Field label={t("form.field.aspect-ratio-strategy", "Aspect ratio strategy")} required>
            <Select
              value={metadata.aspect_ratio_strategy}
              options={ASPECT_RATIO_STRATEGIES.map((entry) => ({ value: entry, label: entry }))}
              onChange={(event) =>
                setMetadata({
                  aspect_ratio_strategy: event.target.value as typeof metadata.aspect_ratio_strategy,
                })
              }
            />
          </Field>
          <Field label={t("form.field.dpi", "DPI")}>
            <Input
              type="number"
              min={50}
              value={metadata.dpi ?? ""}
              onChange={(event) =>
                setMetadata({ dpi: event.target.value === "" ? null : Number(event.target.value) })
              }
            />
          </Field>
          <Field label={t("form.field.max-print-width", "Max print width")}>
            <Input value={metadata.max_print_width} readOnly disabled />
          </Field>
          <Field label={t("form.field.max-print-height", "Max print height")}>
            <Input value={metadata.max_print_height} readOnly disabled />
          </Field>
        </div>
      </Card>
    </div>
  );
}

// ── products ──────────────────────────────────────────────────────────────

function ProductsTab({ rows, loading }: { rows: ProductEntityDTO[]; loading: boolean }) {
  const { t } = useTranslation();
  const cols = useMemo(() => {
    const c = defineColumns<ProductEntityDTO>();
    return [
      c.custom(
        "thumbnail",
        () => "",
        (row) => <Thumbnail url={objectUrl(row.images?.[0]?.image)} alt={row.sku} />,
        { headerKey: "column.thumbnail", filter: "none", enableSorting: false, size: 56 },
      ),
      c.custom(
        "family",
        (row) => row.product?.category?.family ?? "",
        (row) => (
          <EnumBadge value={row.product?.category?.family} variants={PRODUCT_FAMILY_VARIANT} />
        ),
        { headerKey: "column.family", filter: "select", priority: 3 },
      ),
      c.text("sku", (row) => row.sku, { headerKey: "column.sku", priority: 1 }),
      c.text("upc", (row) => row.metadata?.upc ?? "", { headerKey: "column.upc", priority: 4 }),
      c.text("name", (row) => row.product?.code ?? "", { headerKey: "column.name", priority: 2 }),
      c.custom(
        "status",
        (row) => row.product?.status ?? "",
        (row) => <EnumBadge value={row.product?.status} variants={PRODUCT_STATUS_VARIANT} />,
        { headerKey: "column.status", filter: "select", priority: 3 },
      ),
      c.custom(
        "distribution_status",
        (row) => row.product?.distribution_status ?? "",
        (row) => (
          <EnumBadge value={row.product?.distribution_status} variants={DISTRIBUTION_VARIANT} />
        ),
        { headerKey: "column.distribution-status", filter: "select", priority: 4 },
      ),
      c.custom(
        "category",
        (row) => row.product?.category?.name ?? "",
        (row) => (
          <TwoLine
            primary={row.product?.category?.name}
            secondary={row.product?.category?.material}
          />
        ),
        { headerKey: "column.category", filter: "select", priority: 2 },
      ),
    ];
  }, []);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DataTable<ProductEntityDTO>
        data={rows}
        columns={cols}
        getRowId={(row) => row.sku}
        loading={loading}
        emptyMessage={t("table.empty", "Nothing to show")}
      />
    </div>
  );
}

// ── tags ──────────────────────────────────────────────────────────────────

function TagsTab({
  tags,
  onChange,
  artworkId,
}: {
  tags: ArtworkTagDTO[];
  onChange: (tags: ArtworkTagDTO[]) => void;
  artworkId: number;
}) {
  const { t } = useTranslation();

  const cols = useMemo(() => {
    const c = defineColumns<ArtworkTagDTO>();
    return [
      c.custom(
        "type",
        (row) => row.type,
        (row) => <EnumBadge value={row.type} variants={ARTWORK_TAG_VARIANT} />,
        { headerKey: "column.type", filter: "select", priority: 2 },
      ),
      c.text("value", (row) => row.value, { headerKey: "column.tag", priority: 1 }),
    ];
  }, []);

  // Tags belong to the artwork document, so edits stay local until the page's
  // Save writes the whole artwork — hence no client here.
  const editor = useMemo(
    () => ({
      fields: {
        type: selectField<ArtworkTagDTO>(
          (row) => row.type,
          (row, value) => ({ ...row, type: value as ArtworkTagDTO["type"] }),
          ["COLOR", "THEME", "KEYWORD"],
        ),
        value: textField<ArtworkTagDTO>(
          (row) => row.value,
          (row, value) => ({ ...row, value: value.toLowerCase() }),
          true,
        ),
      },
      newRow: () => ({ id: 0, artwork_id: artworkId, type: "COLOR" as const, value: "" }),
      validate: (row: ArtworkTagDTO) => {
        if (!row.value.trim()) {
          return { value: t("form.validation.field.required", "Value is required") };
        }
        if (tags.some((entry) => entry.id !== row.id && entry.value === row.value)) {
          return { value: t("form.validation.field.unique", "Value already exists") };
        }
        return null;
      },
      onSave: (row: ArtworkTagDTO, isNew: boolean) => {
        onChange(
          isNew
            ? [...tags, { ...row, id: row.id || -(tags.length + 1) }]
            : tags.map((entry) => (entry.id === row.id ? row : entry)),
        );
      },
      onDelete: (row: ArtworkTagDTO) => onChange(tags.filter((entry) => entry.id !== row.id)),
      deleteConfirm: () => ({ title: t("dialog.confirm.delete.title", "Confirm delete") }),
    }),
    [artworkId, onChange, t, tags],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DataTable<ArtworkTagDTO>
        data={tags}
        columns={cols}
        getRowId={(row) => String(row.id)}
        editor={editor}
      />
    </div>
  );
}

// ── images ────────────────────────────────────────────────────────────────

function ImagesTab({ artwork, onChanged }: { artwork: ArtworkDTO; onChanged: () => void }) {
  const { t } = useTranslation();
  const api = useApi();
  const confirm = useConfirm();
  const [selected, setSelected] = useState<ArtworkImageDTO[]>([]);
  const [picker, setPicker] = useState(false);
  const [uploading, setUploading] = useState(false);

  const correctedInput = useRef<HTMLInputElement>(null);
  const hasCorrected = (artwork.images ?? []).some((entry) => entry.type === "HR_CORRECTED");

  const cols = useMemo(() => {
    const c = defineColumns<ArtworkImageDTO>();
    return [
      c.custom(
        "thumbnail",
        () => "",
        (row) =>
          row.type.startsWith("HR_") ? (
            <Thumbnail url={null} fallback="HR" />
          ) : (
            <Thumbnail url={objectUrl(row.image)} size="md" />
          ),
        { headerKey: "column.thumbnail", filter: "none", enableSorting: false, size: 64 },
      ),
      c.custom(
        "type",
        (row) => row.type,
        (row) => <EnumBadge value={row.type} variants={ARTWORK_IMAGE_VARIANT} />,
        { headerKey: "column.type", filter: "select", priority: 1 },
      ),
      c.custom(
        "dimensions",
        (row) => row.width,
        (row) => <TwoLine primary={`${row.width} x ${row.height}`} secondary="pixels" />,
        { headerKey: "column.dimensions", filter: "number", priority: 3 },
      ),
      c.custom("url", (row) => row.image?.url ?? "", (row) => <CopyUrl url={row.image?.url} />, {
        headerKey: "column.url",
        filter: "none",
        priority: 4,
      }),
    ];
  }, []);

  const uploadCorrected = async (file: File) => {
    setUploading(true);
    try {
      await api.artworkImages.uploadCorrectedImage(artwork.id, file);
      toast.success(
        t("notification.upload-corrected-image.success", "Corrected image uploaded successfully"),
      );
      onChanged();
    } catch (failure) {
      toast.error(
        failure instanceof Error
          ? failure.message
          : t("notification.upload-corrected-image.error", "Failed to upload corrected image"),
      );
    } finally {
      setUploading(false);
    }
  };

  const deleteCorrected = async () => {
    const ok = await confirm({
      title: t("dialog.confirm.delete-corrected-image.title", "Delete corrected image"),
      message: t(
        "dialog.confirm.delete-corrected-image.text",
        "Are you sure you want to delete the corrected image ?",
      ),
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.artworkImages.deleteCorrectedImage(artwork.id);
      toast.success(
        t("notification.delete-corrected-image.success", "Corrected image deleted successfully"),
      );
      onChanged();
    } catch (failure) {
      toast.error(
        failure instanceof Error
          ? failure.message
          : t("notification.delete-corrected-image.error", "Failed to delete corrected image"),
      );
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DataTable<ArtworkImageDTO>
        data={artwork.images ?? []}
        columns={cols}
        getRowId={(row) => String(row.id)}
        rowHeight={52}
        selectable
        onSelectionChange={setSelected}
        onRowClick={(row) => {
          const url = objectUrl(row.image);
          if (url) window.open(url, "_blank", "noreferrer");
        }}
        actions={[
          {
            label: t("button.add-to-display-collection", "Add to display collection"),
            icon: <FolderPlus className="size-4" />,
            variant: "primary",
            write: true,
            requiresSelection: true,
            onClick: () => setPicker(true),
          },
          {
            label: t("button.upload-corrected-image", "Upload corrected image"),
            icon: <Upload className="size-4" />,
            variant: "primary",
            write: true,
            disabled: uploading,
            onClick: () => correctedInput.current?.click(),
          },
          {
            label: t("button.delete-corrected-image", "Delete corrected image"),
            icon: <Trash2 className="size-4" />,
            variant: "danger",
            write: true,
            disabled: !hasCorrected,
            onClick: () => void deleteCorrected(),
          },
        ]}
      />

      {/* The corrected image replaces the HR original, so the picker is a
          direct upload rather than a dialog — the warning lives on the list
          screen's own dialog, which is where a bulk operator starts. */}
      <input
        ref={correctedInput}
        type="file"
        hidden
        accept=".jpg,.jpeg,.tif,.tiff,image/jpeg,image/tiff"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) void uploadCorrected(file);
        }}
      />

      <DisplayCollectionPickerDialog
        open={picker}
        onClose={() => setPicker(false)}
        onConfirm={async (collectionId, name) => {
          const collection = await api.displayCollections.findById(collectionId);
          if (!collection) return;
          const images = [...(collection.images ?? [])];
          for (const image of selected) {
            const present = images.some(
              (line) => line.owner === "ARTWORK" && line.image_id === image.id,
            );
            if (!present) {
              images.push({
                id: 0,
                display_collection_id: collectionId,
                image_id: image.id,
                owner: "ARTWORK",
                sort_order: images.length,
              });
            }
          }
          try {
            await api.displayCollections.update({ ...collection, images });
            toast.success(
              `${t("notification.add-image-display-collection.success", "Image added to display collection successfully")} "${name}"`,
            );
          } catch (failure) {
            toast.error(
              failure instanceof Error
                ? failure.message
                : t(
                    "notification.add-image-display-collection.error",
                    "Failed to add image to display collection",
                  ),
            );
          }
        }}
      />
    </div>
  );
}

// ── product images ────────────────────────────────────────────────────────

function ProductImagesTab({
  entities,
  loading,
}: {
  entities: ProductEntityDTO[];
  loading: boolean;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const [selected, setSelected] = useState<ProductImageDTO[]>([]);
  const [picker, setPicker] = useState(false);

  // CUSTOM-ratio products have no rendered decor image, so they are excluded —
  // the same filter the Vaadin layout applied before handing the grid its rows.
  const usable = entities.filter((entry) => entry.product && entry.product.ratio !== "CUSTOM");
  const rows = usable.flatMap((entry) => entry.images ?? []);
  const productById = new Map(usable.map((entry) => [entry.product.id, entry.product]));

  const cols = useMemo(() => {
    const c = defineColumns<ProductImageDTO>();
    return [
      c.custom(
        "thumbnail",
        () => "",
        (row) => <Thumbnail url={objectUrl(row.image)} size="md" />,
        { headerKey: "column.thumbnail", filter: "none", enableSorting: false, size: 64 },
      ),
      c.text("product", (row) => productById.get(row.product_id)?.code ?? "", {
        headerKey: "column.product",
        filter: "select",
        priority: 1,
      }),
      c.custom(
        "type",
        (row) => row.type,
        (row) => <Badge variant="info">{row.type}</Badge>,
        { headerKey: "column.type", filter: "select", priority: 2 },
      ),
      c.custom("url", (row) => row.image?.url ?? "", (row) => <CopyUrl url={row.image?.url} />, {
        headerKey: "column.url",
        filter: "none",
        priority: 4,
      }),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entities]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <DataTable<ProductImageDTO>
        data={rows}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={loading}
        rowHeight={52}
        selectable
        onSelectionChange={setSelected}
        onRowClick={(row) => {
          const url = objectUrl(row.image);
          if (url) window.open(url, "_blank", "noreferrer");
        }}
        actions={[
          {
            label: t("button.add-to-display-collection", "Add to display collection"),
            icon: <FolderPlus className="size-4" />,
            variant: "primary",
            write: true,
            requiresSelection: true,
            onClick: () => setPicker(true),
          },
        ]}
      />

      <DisplayCollectionPickerDialog
        open={picker}
        onClose={() => setPicker(false)}
        onConfirm={async (collectionId, name) => {
          const collection = await api.displayCollections.findById(collectionId);
          if (!collection) return;
          const images = [...(collection.images ?? [])];
          for (const image of selected) {
            const present = images.some(
              (line) => line.owner === "PRODUCT" && line.image_id === image.id,
            );
            if (!present) {
              images.push({
                id: 0,
                display_collection_id: collectionId,
                image_id: image.id,
                owner: "PRODUCT",
                sort_order: images.length,
              });
            }
          }
          try {
            await api.displayCollections.update({ ...collection, images });
            toast.success(
              `${t("notification.add-image-display-collection.success", "Image added to display collection successfully")} "${name}"`,
            );
          } catch (failure) {
            toast.error(
              failure instanceof Error
                ? failure.message
                : t(
                    "notification.add-image-display-collection.error",
                    "Failed to add image to display collection",
                  ),
            );
          }
        }}
      />
    </div>
  );
}

// ── shopify ───────────────────────────────────────────────────────────────

/**
 * The artwork's listing on one storefront: its live status, price range and
 * variants, or a create button when it was never listed.
 */
function ShopifyTab({ artwork, store }: { artwork: ArtworkDTO; store: StoreDTO }) {
  const { t } = useTranslation();
  const api = useApi();
  const sync = (artwork.store_syncs ?? []).find((entry) => entry.store?.id === store.id);

  const product = useQuery({
    queryKey: ["shopify", "product", store.id, artwork.id],
    queryFn: () => api.shopifyProducts.get(store.id, artwork.id),
    enabled: Boolean(sync),
  });

  const variants = product.data?.variants?.nodes ?? [];

  const cols = useMemo(() => {
    const c = defineColumns<(typeof variants)[number]>();
    return [
      c.custom(
        "image",
        () => "",
        (row) => <Thumbnail url={row.image?.url ?? null} />,
        { headerKey: "column.image", filter: "none", enableSorting: false, size: 56 },
      ),
      c.text("title", (row) => row.title, { headerKey: "column.name", priority: 1 }),
      c.text("sku", (row) => row.sku, { headerKey: "column.sku", priority: 1 }),
      c.text("barcode", (row) => row.barcode, { headerKey: "column.barcode", priority: 4 }),
      c.text(
        "options",
        (row) => (row.selectedOptions ?? []).map((o) => `${o.name}: ${o.value}`).join(", "),
        { headerKey: "column.custom-options", enableSorting: false, priority: 5 },
      ),
      c.text("price", (row) => row.price, { headerKey: "column.price", align: "right", priority: 2 }),
      c.text("compare_at_price", (row) => row.compareAtPrice, {
        headerKey: "column.compare-at-price",
        align: "right",
        priority: 4,
      }),
    ];
  }, []);

  if (!sync) {
    return (
      <div className="flex min-h-64 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-line">
        <p className="text-[13px] text-ink-muted">
          {t("value.not-listed", "This artwork is not listed on this store.")}
        </p>
        <Button
          variant="primary"
          icon={<Upload className="size-4" />}
          disabled={artwork.status !== "ACTIVE"}
          onClick={() =>
            toast.info(
              t(
                "notification.shopify-create-from-grid",
                "Use the artwork grid's “Add to shopify” action to list this piece.",
              ),
            )
          }
        >
          {t("button.create", "Create")}
        </Button>
      </div>
    );
  }

  // The listing's state, then its variants — the two panels the nexpim product
  // page shows for the same thing, so the two apps read the same way.
  const priceRange = product.data?.priceRange;
  return (
    <div className="flex flex-col gap-4">
      <Card className="p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[15px] font-semibold">{product.data?.title ?? artwork.metadata.title ?? ""}</span>
          <Badge variant="success" size="sm">
            {product.data?.status ?? "—"}
          </Badge>
          <Badge variant={sync.status === "ACTIVE" ? "success" : "error"} size="sm">
            {sync.status === "ACTIVE"
              ? t("shopify.status.published", "Published")
              : t("shopify.status.unpublished", "Unpublished")}
          </Badge>
          {product.data?.onlineStoreUrl && (
            <a
              href={product.data.onlineStoreUrl}
              target="_blank"
              rel="noreferrer"
              className="text-[12px] font-medium text-accent hover:underline"
            >
              {t("value.url", "Link")}
            </a>
          )}
        </div>
        <p className="mt-1 text-[12px] text-ink-faint">
          {product.data?.id ?? "—"}
          {priceRange
            ? ` · $${priceRange.minVariantPrice.amount} – $${priceRange.maxVariantPrice.amount}`
            : ""}
          {product.data?.updatedAt ? ` · ${t("shopify.updated-at", "Updated")} ${product.data.updatedAt}` : ""}
        </p>
      </Card>

      <Card className="overflow-hidden">
        <h3 className="px-5 pt-5 pb-3 text-[13px] font-semibold text-ink">{t("shopify.variants", "Variants")}</h3>
        <div className="flex min-h-72 flex-col">
          <DataTable
            data={variants}
            columns={cols}
            getRowId={(row) => row.id ?? row.sku ?? Math.random().toString(36)}
            loading={product.isLoading}
          />
        </div>
      </Card>
    </div>
  );
}

/** Re-exported so the router can lazily reach the CRUD helper's types. */
export { crudEditor };
